/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { first, filter } from 'rxjs/operators';

//just types
import { Ticket, TicketRenderTypes } from '@touchplan/common';
import { PromisePinData } from '@touchplan/common';
// import { PlanState } from "src/app/services/plan-state.service";
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';

import {
	TicketActionTypes,
	FancyTicketAction,
	TicketDuplicatedAction,
	TicketLiveUpdateAction,
	FloatingTicketSavedAction,
	TicketFocusedAction,
} from '../actions/ticket-actions';

import { PlanState } from 'src/app/services/plan-state.service';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { ListLike } from 'src/app/app-data/interfaces/list-like';
import { logger } from '@touchplan/logging';
import { DependencyLinkageType } from 'src/app/app-data/models/DependencyLinkageType';
import { hasRestrictedAccess } from 'src/app/services/plan-access-check-utils';

/**
 * ---------------------------------------
 * h1. DO NOT USE ARROW FUNCTIONS IN THIS FILE
 * also, this === planState
 * ---------------------------------------
 */

// interface listLike{
// 	forEach: typeof Array.prototype.forEach
// }

//types/ interfaces
type TicketOrTicketArray = Ticket | ListLike<Ticket>;
function isTicketArray(tick: any): tick is TicketOrTicketArray {
	return tick.forEach !== undefined;
}

/**
 * Take an action builder which accepts a ticketList and spilts the list into multiple actions
 * @param  selectedTickets
 */
const _multiplexTicketArray = (
	planState: PlanState,
	selectedTickets: TicketOrTicketArray,
	actionType: TicketActionTypes,
	ticketListOverride?: TicketList,
	config?: any
) => {
	if (!selectedTickets) {
		logger.log('boom');
		debugger;
		return;
	}
	const arr = forceArray(selectedTickets);
	arr.forEach(function (t: Ticket) {
		const action = new FancyTicketAction(t, actionType);
		if (config) {
			(action as any).config = config;
		}

		if (ticketListOverride) {
			ticketListOverride.appEvent$.next(action);
		} else if (t.isTimelineTicket) {
			planState.timelineTickets.appEvent$.next(action);
		} else {
			planState.tickets.appEvent$.next(action);
		}
	});
};

function forceArray(selectedTickets: TicketOrTicketArray): ListLike<Ticket> {
	return isTicketArray(selectedTickets) ? selectedTickets : ([selectedTickets] as any);
}

/** Change the passed in ticket(s) to edit view */
export function changeTicketViewToEdit(
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketChangedToEdit,
		ticketListOverride
	);
}
/** Change the passed in ticket(s) to edit view */
export function changeTicketViewToDrag(selectedTickets: TicketOrTicketArray, isUndoable: boolean = true) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketChangedToDrag, null, {
		isUndoable,
	});
}
/** Change the passed in ticket(s) to edit locations view */
export function changeTicketViewToLocationsEdit(
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketChangedToLocationsEdit,
		ticketListOverride
	);
}

/** Change the passed in ticket(s) to default view */
export const changeTicketViewToDefault = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketChangedToDefault,
		ticketListOverride
	);
};

/** Change the passed in ticket(s) to default view */
export const changeTicketViewDataToDefault = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketViewDataResetToDatabase,
		ticketListOverride
	);
};

export const selectTicket = function (selectedTickets: TicketOrTicketArray, ticketListOverride?: TicketList) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketSelected, ticketListOverride);
};

export const selectCycleTicket = function (selectedTicket: Ticket, ticketListOverride?: TicketList) {
	return _multiplexTicketArray(
		this,
		selectedTicket,
		TicketActionTypes.cycleTicketSelected,
		ticketListOverride
	);
};

export const unSelectTicket = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketUnselected, ticketListOverride);
};

export const unselectCycleTicket = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.cycleTicketUnselected,
		ticketListOverride
	);
};

export const clearSelection = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	const safeTicketList = new Map(forceArray(selectedTickets) as Map<string, Ticket>);
	unSelectTicket.call(this, safeTicketList, ticketListOverride);
	unselectCycleTicket.call(this, safeTicketList, ticketListOverride);
};

//this does not currently support timeline tickets
export const selectTicketDelayed = function (idOrIdArray) {
	if (!idOrIdArray) {
		return;
	}
	const arr: string[] = idOrIdArray.length ? idOrIdArray : [idOrIdArray];
	arr.forEach((id) => {
		this.tickets.appEvent$.next(new FancyTicketAction(id, TicketActionTypes.ticketSelectionDelayed, id));
	});
};

export const focusTicket = function (
	selectedTickets: TicketOrTicketArray,
	config?: TicketFocusedAction['config'],
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketFocused,
		ticketListOverride,
		config
	);
};

export function selectTicketToggle(selectedTickets: TicketOrTicketArray, ticketListOverride?: TicketList) {
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	const self = this;
	const arr = forceArray(selectedTickets);
	arr.forEach(function (t) {
		if (t.selected) {
			ticketListOverride.appEvent$.next(new FancyTicketAction(t, TicketActionTypes.ticketUnselected));
		} else {
			ticketListOverride.appEvent$.next(new FancyTicketAction(t, TicketActionTypes.ticketSelected));
		}
	});
}

//since this should execute syncronously anyways... lets try something
export function addFloatingTicketToPlan(startingData: any) {
	let ticketAction;
	this.tickets.rawEvent$
		.pipe(
			filter((act) => (<any>act).type === TicketActionTypes.floatingTicketAdded),
			first()
		)
		.subscribe((tick) => {
			ticketAction = tick;
		});
	this.tickets.appEvent$.next(new FancyTicketAction(startingData, TicketActionTypes.floatingTicketAdded));
	return ticketAction;
}
/** This applies to both originally floating tickets and normal plan tickets, the "backend" gets to differentiate */
export function moveTicketToFloating(selectedTickets: TicketOrTicketArray) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketMovedToFloating);
}
export function returnTicketToPlan(selectedTickets: TicketOrTicketArray) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketReturnedToPlan);
}
export function saveFloatingTicketToPlan(ticket: Ticket, id?: string) {
	const action = new FancyTicketAction(
		ticket,
		TicketActionTypes.floatingTicketSaved
	) as FloatingTicketSavedAction;
	if (id) {
		action.config = { ticketId: id };
	}

	this.tickets.appEvent$.next(action);
}
/** Technically this is a no-op, but it allows the rest of the app to react instead of just ticket_handling */
export function saveTicketToFloating(selectedTickets: TicketOrTicketArray) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketSavedToFloating);
}

/** Legacy */
export const ticketDragged = function (ticketData: any, ticketId: string) {
	this.tickets.appEvent$.next(new FancyTicketAction(ticketData, TicketActionTypes.ticketDragged, ticketId));
};
/** Modify local ticket data, applies your passed in data to both the selected renderType and the view. */
// export const updateTicketLiveData = function(ticketId: string, ticketData: any, ticketListOverride?: TicketList, ticketViewOverride?: TicketRenderTypes){
export const updateTicketLiveData = function (
	ticketId: string,
	ticketData: any,
	ticketListOverride?: TicketList,
	config?: TicketLiveUpdateAction['config']
) {
	// debugger;
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}

	const action = new FancyTicketAction(ticketData, TicketActionTypes.ticketLiveUpdate, ticketId);
	if (config) {
		(action as any).config = config;
	}
	// if(ticketViewOverride){ (action as any).config = {viewOverride: ticketViewOverride} }

	ticketListOverride.appEvent$.next(action);
};

/** Modify local ticket data, applies your passed in data to both the selected renderType and the view. */
export const updateTicketEditData = function (
	ticketId: string,
	ticketData: any,
	ticketListOverride?: TicketList
) {
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	ticketListOverride.appEvent$.next(
		new FancyTicketAction(ticketData, TicketActionTypes.ticketEditUpdate, ticketId)
	);
};
//TODO - fix this null thing
/** Copies local data to database and persists to firebase */
export const persistTicketData = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList,
	ticketViewOverride?: TicketRenderTypes
) {
	const config: any = ticketViewOverride ? { viewOverride: ticketViewOverride } : {};

	const planId = this.planId;
	const project = this.project.syncObject;
	const planAccess = this.plansApi.planAccessApi.data.syncList.get(planId);
	const authedProjectMember = this.authedProjectMemberSync;
	const isReadOnly = hasRestrictedAccess(project, planAccess, authedProjectMember);
	if (isReadOnly && ticketListOverride !== this.floatingTickets) {
		config.readOnlyWrite = true;
	}

	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.ticketPersistData,
		ticketListOverride,
		config
	);
	// this.tickets.appEvent$.next(new FancyTicketAction(null, TicketActionTypes.ticketPersistData, ticketId));
};

/** only use this if your going to do something else with the data, since this one will not trigger a notification */
export const persistTicketDataLocalOnly = function (selectedTickets: TicketOrTicketArray) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketPersistDataLocalOnly);
};

export const deleteTicket = function (selectedTickets: TicketOrTicketArray, ticketListOverride?: TicketList) {
	return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.deleteTicket, ticketListOverride);
};

// non user action - used for internally delete ticket without registering undo operators
export const internalDeleteTicket = function (
	selectedTickets: TicketOrTicketArray,
	ticketListOverride?: TicketList
) {
	return _multiplexTicketArray(
		this,
		selectedTickets,
		TicketActionTypes.internalDeleteTicket,
		ticketListOverride
	);
};

export const addTicket = function (ticketData: any, ticketListOverride?: TicketList, config?) {
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	const action = new FancyTicketAction(ticketData, TicketActionTypes.addTicket);
	(action as any).config = config || {};
	ticketListOverride.appEvent$.next(action);
};

export const duplicateTicket = function (
	selectedTickets: TicketOrTicketArray,
	config?: TicketDuplicatedAction['config'],
	ticketListOverride?: TicketList
) {
	if (!config) {
		config = {} as any;
	}
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	const action = new FancyTicketAction(selectedTickets, TicketActionTypes.ticketDuplicatedInput, 'no');
	(action as any).config = config;
	return ticketListOverride.appEvent$.next(action);

	// return _multiplexTicketArray(this, selectedTickets, TicketActionTypes.ticketDuplicated, ticketListOverride, config);
};

/** This will never be an output action, but instead map to a series of movedToFloating actions */
export const removeFloatingTickets = function () {
	this.tickets.appEvent$.next(new FancyTicketAction(null, TicketActionTypes.removeFloatingTickets, 'fake'));
};

export const createTicketWithId = function (
	ticketId: string,
	ticketData: any,
	ticketListOverride?: TicketList,
	config?
) {
	// debugger;
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	const action = new FancyTicketAction(ticketData, TicketActionTypes.createTicketWithId, ticketId);
	(action as any).config = config || {};
	ticketListOverride.appEvent$.next(action);
};

//accepting a ticket list doesn't feel logical here
export const addPinToTicket = function (
	ticket: Ticket,
	pinData: PromisePinData,
	ticketListOverride?: TicketList
) {
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}

	//decorate pin with time/user stamp
	pinData.createdAt = fbdbTime.syncTime;
	pinData.createdBy = this.authedProjectMemberSync.view.user?.$id;

	const action = new FancyTicketAction(ticket, TicketActionTypes.addPinToTicket);
	(action as any).pinData = pinData;
	ticketListOverride.appEvent$.next(action);
};
export const removePinFromTicket = function (ticket: Ticket, ticketListOverride?: TicketList, config?: any) {
	if (!ticketListOverride) {
		ticketListOverride = this.tickets;
	}
	const action = new FancyTicketAction(ticket, TicketActionTypes.removePinFromTicket);
	(action as any).config = config || {};
	ticketListOverride.appEvent$.next(action);
};

/**
 * Set a dependency in the simplest, most common way, starting at the src, to the tapped one. Toggling, and persisting.
 * @param  tappedTicketId String or Ticket corresponding to the ticket tapped on
 */
// export const simpleDependencyToggle = function(this:PlanState, tappedTicketId: string | Ticket){
export const simpleDependencyToggle = function (
	tappedTicketId: string | Ticket,
	linkageType = DependencyLinkageType.FinishToStart,
	lagDays = 0,
	persist = false
) {
	if (this.dependencies.syncMode && this.dependencies.syncMode.target) {
		let tappedTicket = tappedTicketId as Ticket;
		if (typeof tappedTicketId === 'string') {
			tappedTicket = this.dependencies.checkTicketCache(tappedTicketId);
		}
		const srcTicketId = this.dependencies.syncMode.target;
		const srcTicket = this.dependencies.checkTicketCache(srcTicketId);
		const predData = this.dependencies.togglePredecessor(tappedTicket.$id, linkageType, lagDays);
		logger.log('pred data', predData);
		if (!predData) {
			return;
		}

		//type 2: final version, just applies it to the main view for just predecessors
		this.actions.updateTicketLiveData(
			srcTicket.$id,
			predData,
			srcTicket.isTimelineTicket ? this.timelineTickets : this.tickets
		);

		if (persist) {
			this.actions.persistTicketData(
				srcTicket,
				srcTicket.isTimelineTicket ? this.timelineTickets : this.tickets,
				TicketRenderTypes.database
			);
		}
	}
};

export const abortEdit = function () {
	const self = this;
	[this.tickets, this.floatingTickets].forEach(function (ticketList) {
		const edits = ticketList.editTicket$.getValue();
		if (edits && edits.size > 0) {
			changeTicketViewToDefault.call(self, Array.from(edits.values()), ticketList);
		}
	});

	//just check the normal ticket list for now
	const selections = this.tickets.selectedTicket$.getValue().list;
	if (selections && selections.size === 1) {
		unSelectTicket.call(self, selections);
	}
};
